import {JsonProperty} from "@/shared/json/json-property";
import {JsonObject} from "@/shared/json/json-object";
import {md5} from "../shared/md5";
import {
  BasePracticeListItem,
  BasePracticeListItemsLoader,
  BasePracticeProfileListItem,
  BasePracticeProfileListItemsLoader,
  PracticeData,
  PracticeProfileType,
  SCHEDULE_TIME_KTM,
  ScheduleEntrySession
} from "@/libwyze-shared/types";
import {
  AddressSectionData,
  ContactSectionData,
  FaqSectionData,
  FooterSectionData,
  GallerySectionData,
  HeaderSectionData,
  HeroSectionData,
  HighlightSectionData,
  PromoSectionData,
  ServicesSectionData,
  SiteTemplatePageSections,
  StatsSectionData,
  TeamSectionData,
  TestimonialsSectionData
} from "./page-components/types";
import {
  BentoOutlined,
  EditAttributesOutlined,
  Facebook,
  Google,
  Instagram,
  LinkedIn,
  YouTube,
} from "@mui/icons-material";
import {$KTM, $KTS, ColorValue, KeyMetadatas, KeyTextStrings, ObjectConfig, TypedObject} from "@/shared/types";
import {SvgIcon} from "@mui/material";
import {FormGen} from "../shared/formgen";
import {JSON_OBJECT, Type} from "@/shared/json/helpers";
import {md5_uuid} from "../common/md5";
import {black, colorPastelGreen, gray} from "../shared/colors";

export enum EditorTool {
  TEMPLATE = "background",
  ATTRIBUTES = "attributes",
}

export type EditorToolMetadata = {
  text: string,
  iconType: typeof SvgIcon,
}

export const EDITOR_TOOLS_KTM = new KeyMetadatas<EditorToolMetadata>([
  $KTM(EditorTool.ATTRIBUTES, {text: "Attributes", iconType: EditAttributesOutlined}),
  $KTM(EditorTool.TEMPLATE, {text: "Template", iconType: BentoOutlined}),
]);

@JsonObject()
export class MediaImage {

  @JsonProperty()
  readonly id: string;

  @JsonProperty()
  readonly name: string;

  @JsonProperty()
  readonly url: string;

  static createNew(name: string, url: string): MediaImage {
    return new MediaImage(md5(url), name, url);
  }

  constructor(id: string, name: string, url: string) {
    this.id = id;
    this.name = name;
    this.url = url;
  }
}

@JsonObject()
export class Media {

  @JsonProperty()
  readonly images: MediaImage[] = [];
}

@JsonObject()
export class WebPageSection {

  @JsonProperty()
  name: (keyof SiteTemplatePageSections);

  @JsonProperty({
    afterDeserialize: (property, currentInstance) => {
      switch ((currentInstance as WebPageSection).name) {
        case "AddressSection":
          return AddressSectionData.from(property);
        case "ContactSection":
          return ContactSectionData.from(property);
        case "FaqSection":
          return FaqSectionData.from(property);
        case "FooterSection":
          return FooterSectionData.from(property);
        case "GallerySection":
          return GallerySectionData.from(property);
        case "HeaderSection":
          return HeaderSectionData.from(property);
        case "HeroSection":
          return HeroSectionData.from(property);
        case "HighlightSection":
          return HighlightSectionData.from(property);
        case "PromoSection":
          return PromoSectionData.from(property);
        case "ServicesSection":
          return ServicesSectionData.from(property);
        case "StatsSection":
          return StatsSectionData.from(property);
        case "TeamSection":
          return TeamSectionData.from(property);
        case "TestimonialsSection":
          return TestimonialsSectionData.from(property);
      }
    }
  })
  data: any;
}

@JsonObject()
export class WebPage {

  @JsonProperty()
  title: string = "My Web Page";

  @JsonProperty({dataStructure: "array", type: WebPageSection})
  readonly sections: WebPageSection[] = [];

  @JsonProperty()
  readonly media: Media = new Media();
}

@JsonObject()
export abstract class BaseVersion {

  @JsonProperty()
  webPage: WebPage;
}

@JsonObject()
export class DraftVersion extends BaseVersion {
}

@JsonObject()
export class PublishedVersion extends BaseVersion {
}

@JsonObject()
export class SitePages {

  @JsonProperty()
  draft: DraftVersion;

  @JsonProperty()
  published: PublishedVersion;
}

export type SiteData2 = {
  practice: {
    name: string,
    profilePhoto: string,
    logo: string,
    phoneNumber: string,
    email: string,
    photos: string[],
    address: {
      line1: string,
      line2: string,
    },
    calendar: {
      entries: {
        days: string,
        hours: string,
      }[],
    },
  },
  providers: {
    displayName: string,
    profilePhoto: string,
    qualifications: string,
    about: string,
  }[],
}

export function SessionText(session: ScheduleEntrySession): string | null {
  if (!session?.startTime || !session?.endTime) {
    return null;
  }
  return `${SCHEDULE_TIME_KTM.findMetadata(session.startTime).text} - ${SCHEDULE_TIME_KTM.findMetadata(session.endTime).text}`;
}

@JsonObject()
export abstract class BaseVantageObject extends TypedObject {

  constructor(readonly practiceId: string) {
    super(null, null);
  }

  protected objectConfig(): ObjectConfig | null {
    return {
      shared: true,
    };
  }

  protected basePath(): string | null {
    return `practiceData/${this.practiceId}/vantage`;
  }
}

export abstract class BaseVantagePracticeListItem extends BasePracticeListItem {

  constructor(id: string) {
    super(id);
  }
}

export abstract class BaseVantagePracticeListItemsLoader<T extends BaseVantagePracticeListItem> extends BasePracticeListItemsLoader<T> {

  protected practiceDataBasePath(): string {
    return `vantage/${this.vantageDataBasePath()}`;
  }

  protected abstract vantageDataBasePath(): string;
}

export enum CampaignAudience {
  ALL_PATIENTS = "all_patients",
  ALL_CONTACTS = "all_contacts",
}

export const AUDIENCE_KTS = new KeyTextStrings([
  $KTS(CampaignAudience.ALL_PATIENTS, "All patients"),
  $KTS(CampaignAudience.ALL_CONTACTS, "All contacts"),
]);

export enum MessageCampaignChannel {
  EMAIL = "email",
  SMS = "sms",
  WHATSAPP = "whatsapp",
}

export type MessageCampaignChannelMetadata = {
  text: string,
}

export const MESSAGE_CAMPAIGN_CHANNEL_KTM = new KeyMetadatas<MessageCampaignChannelMetadata>([
  $KTM(MessageCampaignChannel.EMAIL, {text: "Email"}),
  $KTM(MessageCampaignChannel.SMS, {text: "SMS"}),
  $KTM(MessageCampaignChannel.WHATSAPP, {text: "WhatsApp"}),
]);

export enum MessageCampaignName {
  BIRTHDAY = "birthday",
  FESTIVAL = "festival",
  CUSTOM = "custom",
}

export enum FestivalCampaignTemplateType {
  MAKAR_SANKRANTI = "makar_sankranti",
  REPUBLIC_DAY = "republic_day",
  MAHA_SHIVRATRI = "maha_shivratri",
  HOLI = "holi",
  RAM_NAVAMI = "ram_navami",
  MAHAVIR_JAYANTI = "mahavir_jayanti",
  GOOD_FRIDAY = "good_friday",
  BAISAKHI = "baisakhi",
  BUDDHA_PURNIMA = "buddha_purnima",
  INDEPENDENCE_DAY = "independence_day",
  RAKSHA_BANDHAN = "raksha_bandhan",
  GANESH_CHATURTHI = "ganesh_chaturthi",
  NAVRATRI = "navratri",
  DUSSEHRA = "dussehra",
  DIWALI = "diwali",
  GURU_NANAK_JAYANTI = "guru_nanak_jayanti",
  CHRISTMAS = "christmas",
}

export const CAMPAIGN_FESTIVAL_TYPE_KTS = new KeyTextStrings([
  $KTS(FestivalCampaignTemplateType.MAKAR_SANKRANTI, "Makar Sankranti"),
  $KTS(FestivalCampaignTemplateType.REPUBLIC_DAY, "Republic Day"),
  $KTS(FestivalCampaignTemplateType.MAHA_SHIVRATRI, "Maha Shivratri"),
  $KTS(FestivalCampaignTemplateType.HOLI, "Holi"),
  $KTS(FestivalCampaignTemplateType.RAM_NAVAMI, "Ram Navami"),
  $KTS(FestivalCampaignTemplateType.MAHAVIR_JAYANTI, "Mahavir Jayanti"),
  $KTS(FestivalCampaignTemplateType.GOOD_FRIDAY, "Good Friday"),
  $KTS(FestivalCampaignTemplateType.BAISAKHI, "Baisakhi"),
  $KTS(FestivalCampaignTemplateType.BUDDHA_PURNIMA, "Buddha Purnima"),
  $KTS(FestivalCampaignTemplateType.INDEPENDENCE_DAY, "Independence Day"),
  $KTS(FestivalCampaignTemplateType.RAKSHA_BANDHAN, "Raksha Bandhan"),
  $KTS(FestivalCampaignTemplateType.GANESH_CHATURTHI, "Ganesh Chaturthi"),
  $KTS(FestivalCampaignTemplateType.NAVRATRI, "Navratri"),
  $KTS(FestivalCampaignTemplateType.DUSSEHRA, "Dussehra"),
  $KTS(FestivalCampaignTemplateType.DIWALI, "Diwali"),
  $KTS(FestivalCampaignTemplateType.GURU_NANAK_JAYANTI, "Guru Nanak Jayanti"),
  $KTS(FestivalCampaignTemplateType.CHRISTMAS, "Christmas"),
]);

@JsonObject()
export abstract class BaseMessageCampaignCustomizeOptions extends BaseVantageObject {

  //@ts-ignore
  readonly id: string = `campaign/${this.channel}/${this.name}/customize`;

  @JsonProperty()
  enabled: boolean = false;

  @FormGen({
    name: "Audience",
    type: "enum",
    required: true,
    enumDefaultKeyFirst: true,
    enumValues: AUDIENCE_KTS.values,
    description: "Choose who to send messages",
  })
  @JsonProperty()
  audience: string = CampaignAudience.ALL_PATIENTS;

  constructor(practiceId: string, readonly channel: MessageCampaignChannel, readonly name: MessageCampaignName) {
    super(practiceId);
  }
}

// Email

export abstract class BaseEmailCampaignCustomizeOptions extends BaseMessageCampaignCustomizeOptions {

  constructor(practiceId: string, name: MessageCampaignName) {
    super(practiceId, MessageCampaignChannel.EMAIL, name);
  }
}

@JsonObject()
export abstract class BaseEmailCampaignTemplate {
}


// Email - Birthday

export enum BirthdayEmailCampaignTemplateCoverImage {
  ROYAL_CELEBRRATION = "royal_celebration",
}

export const BIRTHDAY_EMAIL_CAMPAIGN_TEMPLATE_COVER_IMAGE_KTS = new KeyTextStrings([
  $KTS(BirthdayEmailCampaignTemplateCoverImage.ROYAL_CELEBRRATION, "Royal Celebration"),
]);

@JsonObject()
export class BirthdayEmailCampaignTemplate extends BaseEmailCampaignTemplate {

  @FormGen({
    name: "Cover Image",
    type: "enum",
    enumValues: BIRTHDAY_EMAIL_CAMPAIGN_TEMPLATE_COVER_IMAGE_KTS.values,
    enumDefaultKeyFirst: true
  })
  @JsonProperty()
  coverImage: BirthdayEmailCampaignTemplateCoverImage = BirthdayEmailCampaignTemplateCoverImage.ROYAL_CELEBRRATION;
}

@JsonObject()
export class BirthdayEmailCampaignCustomizeOptions extends BaseEmailCampaignCustomizeOptions {

  @FormGen({
    name: "Days before Birthday",
    type: "number",
    numberMin: 0,
    numberMax: 6,
    numberVariant: "slider",
    numberIntegerOnly: true,
    description: "Choose when to send the birthday email relative to the patient's birthday",
  })
  @JsonProperty()
  daysBeforeBirthday: number = 0;

  @FormGen({
    name: "Template",
    type: "object",
  })
  @JsonProperty()
  template: BirthdayEmailCampaignTemplate = new BirthdayEmailCampaignTemplate();

  static async load(practiceId: string): Promise<BirthdayEmailCampaignCustomizeOptions> {
    const obj = new BirthdayEmailCampaignCustomizeOptions(practiceId);
    await obj.load();
    return obj;
  }

  constructor(practiceId: string) {
    super(practiceId, MessageCampaignName.BIRTHDAY);
  }

  protected getType(): Type<any> {
    return BirthdayEmailCampaignCustomizeOptions;
  }
}

/// Email - Festival

@JsonObject()
export class FestivalEmailCampaignTemplate extends BaseEmailCampaignTemplate {

  @FormGen({
    name: "Type",
    type: "enum",
    enumValues: CAMPAIGN_FESTIVAL_TYPE_KTS.values,
  })
  @JsonProperty()
  type: FestivalCampaignTemplateType;
}

@JsonObject()
export class FestivalEmailCampaignCustomizeOptions extends BaseEmailCampaignCustomizeOptions {

  @FormGen({
    name: "Template",
    type: "object",
  })
  @JsonProperty()
  template: FestivalEmailCampaignTemplate = new FestivalEmailCampaignTemplate();

  static async load(practiceId: string): Promise<FestivalEmailCampaignCustomizeOptions> {
    const obj = new FestivalEmailCampaignCustomizeOptions(practiceId);
    await obj.load();
    return obj;
  }

  constructor(practiceId: string) {
    super(practiceId, MessageCampaignName.FESTIVAL);
  }

  protected getType(): Type<any> {
    return FestivalEmailCampaignCustomizeOptions;
  }
}

// SMS

export abstract class BaseSmsCampaignCustomizeOptions extends BaseMessageCampaignCustomizeOptions {

  constructor(practiceId: string, name: MessageCampaignName) {
    super(practiceId, MessageCampaignChannel.SMS, name);
  }
}

@JsonObject()
export abstract class BaseSmsCampaignTemplate {
}

/// SMS - Birthday

@JsonObject()
export class BirthdaySmsCampaignTemplate extends BaseSmsCampaignTemplate {
}

@JsonObject()
export class BirthdaySmsCampaignCustomizeOptions extends BaseSmsCampaignCustomizeOptions {

  @FormGen({
    name: "Days before Birthday",
    type: "number",
    numberMin: 0,
    numberMax: 6,
    numberVariant: "slider",
    numberIntegerOnly: true,
    description: "Choose when to send the birthday sms relative to the patient's birthday",
  })
  @JsonProperty()
  daysBeforeBirthday: number = 0;

  @FormGen({
    name: "Template",
    type: "object",
  })
  @JsonProperty()
  template: BirthdaySmsCampaignTemplate = new BirthdaySmsCampaignTemplate();

  static async load(practiceId: string): Promise<BirthdaySmsCampaignCustomizeOptions> {
    const obj = new BirthdaySmsCampaignCustomizeOptions(practiceId);
    await obj.load();
    return obj;
  }

  constructor(practiceId: string) {
    super(practiceId, MessageCampaignName.BIRTHDAY);
  }

  protected getType(): Type<any> {
    return BirthdaySmsCampaignCustomizeOptions;
  }
}

/// SMS - Festival

@JsonObject()
export class FestivalSmsCampaignTemplate extends BaseSmsCampaignTemplate {

  @FormGen({
    name: "Type",
    type: "enum",
    enumValues: CAMPAIGN_FESTIVAL_TYPE_KTS.values,
  })
  @JsonProperty()
  type: FestivalCampaignTemplateType;
}

@JsonObject()
export class FestivalSmsCampaignCustomizeOptions extends BaseSmsCampaignCustomizeOptions {

  @FormGen({
    name: "Template",
    type: "object",
  })
  @JsonProperty()
  template: FestivalSmsCampaignTemplate = new FestivalSmsCampaignTemplate();

  static async load(practiceId: string): Promise<FestivalSmsCampaignCustomizeOptions> {
    const obj = new FestivalSmsCampaignCustomizeOptions(practiceId);
    await obj.load();
    return obj;
  }

  constructor(practiceId: string) {
    super(practiceId, MessageCampaignName.FESTIVAL);
  }

  protected getType(): Type<any> {
    return FestivalSmsCampaignCustomizeOptions;
  }
}

export abstract class BaseWhatsAppCampaignCustomizeOptions extends BaseMessageCampaignCustomizeOptions {

  constructor(practiceId: string, name: MessageCampaignName) {
    super(practiceId, MessageCampaignChannel.WHATSAPP, name);
  }
}

@JsonObject()
export abstract class BaseWhatsAppCampaignTemplate {
}

/// Whatsapp - Birthday

@JsonObject()
export class BirthdayWhatsAppCampaignTemplate extends BaseWhatsAppCampaignTemplate {
}

@JsonObject()
export class BirthdayWhatsAppCampaignCustomizeOptions extends BaseWhatsAppCampaignCustomizeOptions {

  @FormGen({
    name: "Days before Birthday",
    type: "number",
    numberMin: 0,
    numberMax: 6,
    numberVariant: "slider",
    numberIntegerOnly: true,
    description: "Choose when to send the birthday whatsapp relative to the patient's birthday",
  })
  @JsonProperty()
  daysBeforeBirthday: number = 0;

  @FormGen({
    name: "Template",
    type: "object",
  })
  @JsonProperty()
  template: BirthdayWhatsAppCampaignTemplate = new BirthdayWhatsAppCampaignTemplate();

  static async load(practiceId: string): Promise<BirthdayWhatsAppCampaignCustomizeOptions> {
    const obj = new BirthdayWhatsAppCampaignCustomizeOptions(practiceId);
    await obj.load();
    return obj;
  }

  constructor(practiceId: string) {
    super(practiceId, MessageCampaignName.BIRTHDAY);
  }

  protected getType(): Type<any> {
    return BirthdayWhatsAppCampaignCustomizeOptions;
  }
}

/// Whatsapp - Festival

@JsonObject()
export class FestivalWhatsAppCampaignTemplate extends BaseWhatsAppCampaignTemplate {

  @FormGen({
    name: "Type",
    type: "enum",
    enumValues: CAMPAIGN_FESTIVAL_TYPE_KTS.values,
  })
  @JsonProperty()
  type: FestivalCampaignTemplateType;
}

@JsonObject()
export class FestivalWhatsAppCampaignCustomizeOptions extends BaseWhatsAppCampaignCustomizeOptions {

  @FormGen({
    name: "Template",
    type: "object",
  })
  @JsonProperty()
  template: FestivalWhatsAppCampaignTemplate = new FestivalWhatsAppCampaignTemplate();

  static async load(practiceId: string): Promise<FestivalWhatsAppCampaignCustomizeOptions> {
    const obj = new FestivalWhatsAppCampaignCustomizeOptions(practiceId);
    await obj.load();
    return obj;
  }

  constructor(practiceId: string) {
    super(practiceId, MessageCampaignName.FESTIVAL);
  }

  protected getType(): Type<any> {
    return FestivalWhatsAppCampaignCustomizeOptions;
  }
}

@JsonObject()
export class SiteDataPracticeAddress {

  @FormGen({
    name: "Line 1",
    type: "string",
  })
  @JsonProperty()
  line1: string;

  @FormGen({
    name: "Line 1",
    type: "string",
  })
  @JsonProperty()
  line2: string;
}

@JsonObject()
export class SiteDataPractice {

  @FormGen({
    name: "Name",
    type: "string",
    required: true,
  })
  @JsonProperty()
  name: string;

  @FormGen({
    name: "Logo",
    type: "profile_photo",
    profilePhotoDefaultImageUrl: "/images/default_logo.png",
  })
  @JsonProperty()
  logo: string;

  @FormGen({
    name: "Phone Number",
    type: "phone_number",
    required: true,
  })
  @JsonProperty()
  phoneNumber: string;

  @FormGen({
    name: "Email",
    type: "string",
  })
  @JsonProperty()
  email: string;

  @FormGen({
    name: "Address",
    type: "object",
  })
  @JsonProperty()
  address: SiteDataPracticeAddress = new SiteDataPracticeAddress();
}

@JsonObject()
export class SiteDataProvider {
  @FormGen({
    name: "Full Name",
    type: "string",
  })
  @JsonProperty()
  displayName: string;

  @FormGen({
    name: "Profile Photo",
    type: "profile_photo",
  })
  @JsonProperty()
  profilePhoto: string;

  @FormGen({
    name: "Qualifications / Professional Title",
    type: "string",
  })
  @JsonProperty()
  qualifications: string;

  @FormGen({
    name: "About",
    type: "text",
  })
  @JsonProperty()
  about: string;
}

@JsonObject()
export class SiteData {

  @FormGen({
    name: "Practice",
    type: "object",
  })
  @JsonProperty()
  readonly practice: SiteDataPractice = new SiteDataPractice();

  @FormGen({
    name: "Providers",
    type: "array_object",
    arrayObjectCreator: () => new SiteDataProvider(),
  })
  @JsonProperty()
  readonly providers: SiteDataProvider[] = [];
}

@JsonObject()
export class Site extends BaseVantagePracticeListItem {

  @JsonProperty()
  name: string;

  @JsonProperty()
  templateName: string = "colorado";

  @FormGen({
    name: "",
    type: "object",
  })
  @JsonProperty()
  data: SiteData = new SiteData();

  @JsonProperty()
  readonly pages: SitePages = new SitePages();

  static createNew(name: string, templateName: string): Site {
    return new Site(md5_uuid().substring(0, 6), name, templateName);
  }

  constructor(id: string, name: string, templateName: string) {
    super(id);
    this.name = name;
    this.templateName = templateName;
  }

  draft(): DraftVersion {
    if (!this.pages.draft) {
      this.pages.draft = new DraftVersion();
    }
    return this.pages.draft;
  }

  published(): PublishedVersion {
    if (!this.pages.published) {
      this.pages.published = new PublishedVersion();
    }
    return this.pages.published;
  }

  async save(sites: Sites): Promise<void> {
    await sites.addListItem(this);
  }

  async publish(sites: Sites): Promise<void> {
    this.published().webPage = this.draft().webPage;
    this.pages.draft = undefined;
    await sites.addListItem(this);
  }
}

export class Sites extends BaseVantagePracticeListItemsLoader<Site> {

  constructor(practiceId: string) {
    super(practiceId);
  }

  protected vantageDataBasePath(): string {
    return "sites";
  }

  protected deserializeItem(value: any): Site {
    return JSON_OBJECT.deserializeObject(value, Site);
  }

  protected serializeItem(item: Site): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: Site, item2: Site): number {
    return item1.created - item2.created;
  }
}

export enum SiteDomainType {
  SYSTEM = "system",
  CUSTOM = "custom",
}

export enum SiteDomainStatus {
  NOT_CONNECTED = "not_connected",
  CONNECTED = "connected",
}

export type VantagePracticeSiteDomainStatusMetadata = {
  text: string,
  colorValue?: ColorValue,
}

export const VANTAGE_PRACTICE_SITE_DOMAIN_STATUS_KTM = new KeyMetadatas<VantagePracticeSiteDomainStatusMetadata>([
  $KTM(SiteDomainStatus.NOT_CONNECTED, {text: "Not connected", colorValue: new ColorValue(black, gray)}),
  $KTM(SiteDomainStatus.CONNECTED, {
    text: "Connected",
    colorValue: new ColorValue(black, colorPastelGreen)
  }),
]);

@JsonObject()
export class SiteDomain extends BaseVantagePracticeListItem {

  @JsonProperty()
  readonly type: SiteDomainType;

  @JsonProperty()
  name: string;

  @JsonProperty()
  status: SiteDomainStatus = SiteDomainStatus.NOT_CONNECTED;

  static getDefaultSiteName(practiceId: string, siteId: string): string {
    return `https://vantage-site-${practiceId}-${siteId}.fynehealth.net`;
  }

  static createSystem(name: string, siteId: string): SiteDomain {
    const domain = new SiteDomain(siteId, SiteDomainType.SYSTEM);
    domain.name = name;
    return domain;
  }

  static createCustom(siteId: string): SiteDomain {
    return new SiteDomain(siteId, SiteDomainType.CUSTOM);
  }

  constructor(id: string, type: SiteDomainType) {
    super(id);
    this.type = type;
  }
}

export class SiteDomains extends BaseVantagePracticeListItemsLoader<SiteDomain> {

  constructor(practiceId: string) {
    super(practiceId);
  }

  protected vantageDataBasePath(): string {
    return "siteDomains";
  }

  protected deserializeItem(value: any): SiteDomain {
    return JSON_OBJECT.deserializeObject(value, SiteDomain);
  }

  protected serializeItem(item: SiteDomain): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: SiteDomain, item2: SiteDomain): number {
    return item1.created - item2.created;
  }
}

export enum SocialMediaChannelType {
  FACEBOOK = "facebook",
  INSTAGRAM = "instagram",
  LINKEDIN = "linkedin",
  GOOGLE_BUSINESS = "google_business",
  YOUTUBE = "youtube",
}

export type SocialMediaChannelTypeMetadata = {
  text: string,
  subtext: string,
  iconType: typeof SvgIcon,
}

export const SOCIAL_MEDIA_CHANNEL_TYPE_KTM = new KeyMetadatas<SocialMediaChannelTypeMetadata>([
  $KTM(SocialMediaChannelType.FACEBOOK, {text: "Facebook", subtext: "Page or Group", iconType: Facebook}),
  $KTM(SocialMediaChannelType.INSTAGRAM, {text: "Instagram", subtext: "Business & Creator", iconType: Instagram}),
  $KTM(SocialMediaChannelType.LINKEDIN, {text: "LinkedIn", subtext: "Page or Profile", iconType: LinkedIn}),
  $KTM(SocialMediaChannelType.YOUTUBE, {text: "YouTube", subtext: "Channel", iconType: YouTube}),
  $KTM(SocialMediaChannelType.GOOGLE_BUSINESS, {text: "Google Business Profile", subtext: "Profile", iconType: Google}),
]);

@JsonObject()
export class SocialMediaChannel extends BasePracticeProfileListItem {

  static createNew(type: SocialMediaChannelType, name: string): SocialMediaChannel {
    return new SocialMediaChannel(md5("abc"), type, name);
  }

  static createFromOAuth(type: SocialMediaChannelType, name: string, accessToken: string, refreshToken?: string, profileId?: string, profileUrl?: string): SocialMediaChannel {
    const channel = new SocialMediaChannel(md5_uuid(), type, name);
    channel.accessToken = accessToken;
    channel.refreshToken = refreshToken;
    channel.profileId = profileId;
    channel.profileUrl = profileUrl;
    return channel;
  }

  @JsonProperty()
  readonly type: SocialMediaChannelType;

  @JsonProperty()
  readonly name: string;

  @JsonProperty()
  accessToken: string;

  @JsonProperty()
  refreshToken: string;

  @JsonProperty()
  profileId: string;

  @JsonProperty()
  profileUrl: string;

  constructor(id: string, type: SocialMediaChannelType, name: string) {
    super(id);
    this.type = type;
    this.name = name;
  }
}

const fakeChannel = SocialMediaChannel.createNew(SocialMediaChannelType.GOOGLE_BUSINESS, "Pioneer Pathology");

export class SocialMediaChannels extends BasePracticeProfileListItemsLoader<SocialMediaChannel> {

  protected practiceProfileDataBasePath(): string {
    return "socialMediaChannels";
  }

  getListItems(): SocialMediaChannel[] {
    return [
      ...super.getListItems(),
      fakeChannel,
    ];
  }

  getListItem(id: string): SocialMediaChannel | undefined {
    if (id === fakeChannel.id) {
      return fakeChannel;
    }
    return super.getListItem(id);
  }

  protected serializeItem(item: SocialMediaChannel): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected deserializeItem(value: any): SocialMediaChannel {
    return JSON_OBJECT.deserializeObject(value, SocialMediaChannel);
  }

  protected sortOrder(item1: SocialMediaChannel, item2: SocialMediaChannel): number {
    return item1.created - item2.created;
  }
}

@JsonObject()
export class ScheduledPostGoogleBusinessWhatsNew {

}

export enum ScheduledPostGoogleBusinessType {
  WHATS_NEW = "whats_new",
  OFFER = "offer",
  EVENT = "event",
}

export const SCHEDULED_POST_GOOGLE_BUSINESS_TYPE_KTS = new KeyTextStrings([
  $KTS(ScheduledPostGoogleBusinessType.WHATS_NEW, "What's New"),
  $KTS(ScheduledPostGoogleBusinessType.OFFER, "Offer"),
  $KTS(ScheduledPostGoogleBusinessType.EVENT, "Event"),
]);

export enum ScheduledPostGoogleBusinessButton {
  NONE = "none",
  BOOK = "book",
  ORDER_ONLINE = "order_online",
  BUY = "buy",
  LEARN_MORE = "learn_more",
  SIGN_UP = "sign_up",
  CALL_NOW = "call_now",
}

export type ScheduledPostGoogleBusinessButtonMetadata = {
  text: string,
  hasLink?: boolean,
}

export const SCHEDULED_POST_GOOGLE_BUSINESS_BUTTON_KTM = new KeyMetadatas<ScheduledPostGoogleBusinessButtonMetadata>([
  $KTM(ScheduledPostGoogleBusinessButton.NONE, {text: "None"}),
  $KTM(ScheduledPostGoogleBusinessButton.BOOK, {text: "Book", hasLink: true}),
  $KTM(ScheduledPostGoogleBusinessButton.ORDER_ONLINE, {text: "Order Online", hasLink: true}),
  $KTM(ScheduledPostGoogleBusinessButton.BUY, {text: "Buy", hasLink: true}),
  $KTM(ScheduledPostGoogleBusinessButton.LEARN_MORE, {text: "Learn More", hasLink: true}),
  $KTM(ScheduledPostGoogleBusinessButton.SIGN_UP, {text: "Sign Up", hasLink: true}),
  $KTM(ScheduledPostGoogleBusinessButton.CALL_NOW, {text: "Call Now", hasLink: true}),
]);

@JsonObject()
export class ScheduledPostGoogleBusiness {

  @FormGen({
    name: "Type",
    type: "enum",
    enumVariant: "radio",
    enumValues: SCHEDULED_POST_GOOGLE_BUSINESS_TYPE_KTS.values,
    required: true,
  })
  @JsonProperty()
  type: ScheduledPostGoogleBusinessType;

  @FormGen({
    name: "Text",
    type: "text",
    required: true,
  })
  @JsonProperty()
  text: string;

  @FormGen({
    name: "Button",
    type: "enum",
    enumVariant: "select",
    enumValues: SCHEDULED_POST_GOOGLE_BUSINESS_BUTTON_KTM.kts().values,
    enumDefaultKeyFirst: true,
    required: true,
  })
  @JsonProperty()
  button: string;
}

@JsonObject()
export class ScheduledPost extends BasePracticeProfileListItem {

  static createNew(channelId: string): ScheduledPost {
    return new ScheduledPost(md5_uuid(), channelId);
  }

  @JsonProperty()
  readonly channelId: string;

  @FormGen({
    name: "Post Date & Time",
    type: "datetimenum",
    required: true,
  })
  @JsonProperty()
  dateTime: number;

  @FormGen({
    name: "",
    type: "object",
  })
  @JsonProperty()
  googleBusiness: ScheduledPostGoogleBusiness = new ScheduledPostGoogleBusiness();

  constructor(id: string, channelId: string) {
    super(id);
    this.channelId = channelId;
  }
}

export class ScheduledPosts extends BasePracticeProfileListItemsLoader<ScheduledPost> {

  protected practiceProfileDataBasePath(): string {
    return "scheduledPosts";
  }

  protected serializeItem(item: ScheduledPost): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected deserializeItem(value: any): ScheduledPost {
    return JSON_OBJECT.deserializeObject(value, ScheduledPost);
  }

  protected sortOrder(item1: ScheduledPost, item2: ScheduledPost): number {
    return item1.created - item2.created;
  }
}

////////// Practice Scoped Objects /////////////////

export class VantagePracticeData {

  private static type: PracticeProfileType;
  private static providerInstance: VantagePracticeDataProvider;

  // This must be called whenever current Practice selection changes.
  static async createAndInitProvider(practiceId: string): Promise<void> {
    this.type = PracticeProfileType.PROVIDER;
    this.providerInstance = new VantagePracticeDataProvider(
      practiceId,
    );

    await this.providerInstance.init();
  }

  static shared(): VantagePracticeDataShared {
    switch (this.type) {
      case PracticeProfileType.PROVIDER:
      case PracticeProfileType.STAFF:
        return this.providerInstance;
      default:
        throw new Error("Illegal operation.");
    }
  }

  static provider(): VantagePracticeDataProvider {
    return this.providerInstance;
  }

  private constructor() {
  }
}

export abstract class VantagePracticeDataShared {

  constructor(
    readonly profileType: PracticeProfileType,
    readonly practiceId: string,
  ) {
  }

  async init(): Promise<void> {
  }
}

// These are data types that are scoped to practice + (provider, staff) profile.
export abstract class VantagePracticeProfileDataShared {

  constructor(
    readonly profileId: string,
  ) {
  }

  async init(): Promise<void> {
  }
}

export class VantagePracticeProfileDataProvider extends VantagePracticeProfileDataShared {

  constructor(
    profileId: string,
    readonly socialMediaChannels: SocialMediaChannels,
    readonly scheduledPosts: ScheduledPosts,
  ) {
    super(profileId);
  }
}

export class VantagePracticeDataProvider extends VantagePracticeDataShared {

  constructor(
    practiceId: string,
  ) {
    super(PracticeProfileType.PROVIDER, practiceId);
  }

  private profileData_: VantagePracticeProfileDataProvider;

  profileData(): VantagePracticeProfileDataProvider {
    return this.profileData_;
  }

  async init(): Promise<void> {
    await super.init();
    const provider = PracticeData.provider();
    const practiceId = provider.practiceId;
    const profileId = provider.profileData().profileId;
    this.profileData_ = new VantagePracticeProfileDataProvider(
      profileId,
      new SocialMediaChannels(practiceId, PracticeProfileType.PROVIDER, profileId),
      new ScheduledPosts(practiceId, PracticeProfileType.PROVIDER, profileId),
    );
  }
}
